<template>
  <div class="rx-jump" role="dialog" aria-modal="true" :aria-label="title">
    <div class="rx-jump__header">
      <h2 class="rx-jump__title">{{ title }}</h2>
      <button v-if="!isPick" type="button" class="rx-jump__done" @click="$emit('close')">Done</button>
    </div>
    <div class="rx-jump__scroll">
      <div class="rx-jump__search">
        <i class="fas fa-search rx-jump__search-icon" aria-hidden="true"></i>
        <input
          ref="search"
          v-model="query"
          type="search"
          class="rx-jump__input"
          placeholder="Search properties or folders"
          aria-label="Search properties or folders"
          autocomplete="off" />
      </div>

      <template v-if="!query.trim()">
        <div v-if="!isPick && recents.length > 0">
          <div class="rx-jump__label">Recent</div>
          <div class="rx-jump__chips">
            <button
              v-for="(r, i) in recents"
              :key="r.id"
              type="button"
              :class="['rx-chip', (r.id === currentId || (i === 0 && !hasCurrentRecent)) ? 'rx-chip--current' : '']"
              @click="$emit('navigate-recent', r)">{{ r.label }}</button>
          </div>
        </div>
        <div class="rx-jump__label">All folders</div>
      </template>

      <FolderTree
        variant="phone"
        :nodes="nodes"
        :current-id="isPick ? (selected ? selected.id : '') : currentId"
        :expanded="expanded"
        :query="query"
        :is-disabled="isPick ? isDisabled : null"
        :show-root="isPick"
        @toggle="onToggle"
        @navigate="onNavigate" />
    </div>
    <div v-if="isPick" class="rx-jump__footer">
      <div v-if="selected" class="rx-jump__selected">{{ pickPrompt }} <b>{{ selected.name }}</b></div>
      <div v-else class="rx-jump__selected">Tap a folder to choose it</div>
      <div class="rx-jump__buttons">
        <button type="button" class="rx-jump__btn" :disabled="pending" @click="$emit('close')">Cancel</button>
        <button type="button" class="rx-jump__btn rx-jump__btn--primary" :disabled="!selected || pending" @click="$emit('pick', selected)">
          {{ pending ? 'Moving…' : pickButton }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import FolderTree from './FolderTree.vue'
import { ROOT_ID, pathOf } from './resourcesData'

export default {
  name: 'JumpToFolder',
  components: { FolderTree },
  props: {
    nodes: { type: Object, required: true },
    currentId: { type: String, default: ROOT_ID },
    recents: { type: Array, default: function () { return [] } },
    focusSearch: { type: Boolean, default: false },
    // 'jump' (navigate) or 'pick' (choose a destination, e.g. "Move to…")
    mode: { type: String, default: 'jump' },
    title: { type: String, default: 'Jump to folder' },
    isDisabled: { type: Function, default: null },
    pending: { type: Boolean, default: false },
    // Pick-mode wording (defaults keep the "Move to…" picker unchanged)
    pickPrompt: { type: String, default: 'Move to' },
    pickButton: { type: String, default: 'Move here' }
  },
  data () {
    return {
      selected: null,
      query: '',
      expanded: {}
    }
  },
  computed: {
    isPick () {
      return this.mode === 'pick'
    },
    hasCurrentRecent () {
      var self = this
      return this.recents.some(function (r) { return r.id === self.currentId })
    }
  },
  created () {
    var self = this
    var expanded = {}
    // Expand the ancestors of the current folder so it is visible in the tree.
    pathOf(this.nodes, this.currentId).forEach(function (n) {
      if (n.id !== ROOT_ID && n.id !== self.currentId) expanded[n.id] = true
    })
    this.expanded = expanded
  },
  mounted () {
    var self = this
    document.body.style.overflow = 'hidden'
    if (this.focusSearch) {
      this.$nextTick(function () {
        if (self.$refs.search) self.$refs.search.focus()
      })
    }
  },
  beforeDestroy () {
    document.body.style.overflow = ''
  },
  methods: {
    onToggle (node) {
      var open = !this.expanded[node.id]
      this.$set(this.expanded, node.id, open)
      if (open) this.$emit('load', node)
    },
    onNavigate (node) {
      if (this.isPick) {
        if (!this.pending) this.selected = node
        return
      }
      this.$emit('navigate', node)
    }
  }
}
</script>

<style scoped>
.rx-jump {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1060;
  background: #fff;
  display: flex;
  flex-direction: column;
  color: #1f2937;
}
.rx-jump__header {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 56px;
  padding: 0 8px 0 16px;
  border-bottom: 1px solid #e2e5ea;
}
.rx-jump__title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
}
.rx-jump__done {
  min-width: 44px;
  min-height: 44px;
  border: 0;
  background: transparent;
  color: #1a7f86;
  font-size: 16px;
  font-weight: 600;
  padding: 0 8px;
}
.rx-jump__footer {
  flex: 0 0 auto;
  border-top: 1px solid #e2e5ea;
  padding: 10px 16px calc(10px + env(safe-area-inset-bottom));
  background: #fff;
}
.rx-jump__selected {
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 8px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.rx-jump__buttons {
  display: flex;
}
.rx-jump__btn {
  flex: 1 1 0;
  min-height: 48px;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  background: #fff;
  color: #1f2937;
  font-size: 16px;
  font-weight: 600;
}
.rx-jump__btn + .rx-jump__btn {
  margin-left: 12px;
}
.rx-jump__btn--primary {
  background: #1e7e34;
  border-color: #1e7e34;
  color: #fff;
}
.rx-jump__btn:disabled {
  opacity: 0.5;
}
.rx-jump__scroll {
  flex: 1 1 auto;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 24px;
}
.rx-jump__search {
  position: relative;
  margin: 12px 16px;
}
.rx-jump__search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
}
.rx-jump__input {
  width: 100%;
  height: 44px;
  padding: 0 12px 0 38px;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  font-size: 16px;
  color: #1f2937;
  outline: none;
}
.rx-jump__input:focus {
  border-color: #1a7f86;
}
.rx-jump__label {
  padding: 12px 16px 6px;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}
.rx-jump__chips {
  display: flex;
  overflow-x: auto;
  padding: 0 16px 8px;
  -webkit-overflow-scrolling: touch;
}
.rx-chip {
  flex: 0 0 auto;
  min-height: 44px;
  margin-right: 8px;
  padding: 6px 14px;
  border: 0;
  border-radius: 999px;
  background: #eef0f2;
  color: #1f2937;
  font-size: 14px;
  white-space: nowrap;
}
.rx-chip--current {
  background: #e3f1ef;
  color: #1a7f86;
  font-weight: 600;
}
</style>
